import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SidebarCount } from '@skupreme/api-contracts/core';

import { useUnmappedCount } from '@features/catalog-mapping';
import { usePageVersions } from '@features/page-versions';
import OrganizationsService from '@utility/services/organization';
import type { ApiError } from '@utility/services/base';

export const sidebarCountKeys = {
  all: ['sidebar-count'] as const,
};

/* Before the first answer lands every count is absent, which is the state the
 * menu already draws for one the API leaves out. */
const noCount: Partial<SidebarCount> = {};

/* The menu's counts: the API's, plus the unmapped listings from the mapping API. */
export type MenuCount = Partial<SidebarCount> & { unmappedListings?: number };

/** `isAuthenticated` is null until the session is resolved; that is not a no. */
export function useSidebarCount(
  isAuthenticated: boolean | null,
  wmsPortal: boolean,
) {
  const queryClient = useQueryClient();
  /* The API serves the sidebar from a 60 s cache. After the user changed
   * something, the next answer must skip it; the flag stays up until a fresh
   * answer lands, so a cancelled or failed fetch does not lose it. */
  const freshNext = useRef(false);
  /* The counts are a legacy (Mongo) aggregation the V2 menu does not draw: a
     V2 account never asks, and a legacy one asks once its version is known. */
  const versions = usePageVersions();
  const legacyCounts = versions.known && !versions.isV2('orders');

  const query = useQuery<SidebarCount, ApiError>({
    queryKey: sidebarCountKeys.all,
    queryFn: async ({ signal }) => {
      const fresh = freshNext.current;
      const count = await OrganizationsService.sidebarCount(signal, fresh);
      if (fresh) freshNext.current = false;
      return count;
    },
    enabled: !!isAuthenticated && !wmsPortal && legacyCounts,
  });

  const unmappedListings = useUnmappedCount(!!isAuthenticated && !wmsPortal);
  const data = useMemo<MenuCount>(
    () => ({ ...(query.data ?? noCount), unmappedListings }),
    [query.data, unmappedListings],
  );

  const refreshSidebar = useCallback(() => {
    freshNext.current = true;
    return queryClient.invalidateQueries({ queryKey: sidebarCountKeys.all });
  }, [queryClient]);

  return {
    data,
    loading: query.isFetching,
    refreshSidebar,
  };
}
